import { API_URL } from '@/config';
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Calendar, Image as ImageIcon, Bell, Settings, Crown, ArrowLeft, Menu, X, LogOut, Building2, Newspaper, BookOpen, FileText, ClipboardList, Vote, Database, RefreshCw, Palette, Link2, Award, FileImage, ChevronDown, ChevronRight, BarChart3, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import NotificationBell from '@/components/NotificationBell';

const API = `${API_URL}/api`;
const KANYA_VIVAH_URL = process.env.REACT_APP_KANYA_VIVAH_URL || 'https://kanyavivah.lionsclubhawamahal.com';

const TOAST_MESSAGES = {
  new_vote: (p) => `New vote: ${p.voter_name || 'Someone'} → ${p.nominee_name || 'nominee'}`,
  new_submission: (p) => `New registration: ${p.user_name || 'Someone'} for ${p.event_title || 'event'}`,
  new_member: (p) => `New member joined: ${p.name || 'Someone'}`,
  new_media: (p) => `New media uploaded by ${p.uploader_name || 'Someone'}`,
};

const AdminLayout = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [designMenuOpen, setDesignMenuOpen] = useState(
    location.pathname.includes('/admin/design') || location.pathname === '/admin/forms'
  );

  const isSuperAdmin = user && user.role === 'Super Admin';
  const isTreasurer = user && (user.role === 'Treasurer' || user.role === 'Super Admin');
  const isAdmin = user && ['Super Admin', 'President', 'Secretary'].includes(user.role);

  const { notifications, unreadCount, connected, lastEvent, markAllRead, clearAll } = useRealtimeNotifications();
  const lastEventIdRef = useRef(null);

  useEffect(() => {
    if (lastEvent && lastEvent.id !== lastEventIdRef.current) {
      lastEventIdRef.current = lastEvent.id;
      const msgFn = TOAST_MESSAGES[lastEvent.type];
      if (msgFn) {
        toast.info(msgFn(lastEvent.payload), { duration: 4000 });
      }
    }
  }, [lastEvent]);

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/members', label: 'Members', icon: Users },
    { path: '/admin/events', label: 'Events', icon: Calendar },
  ];

  // Design submenu items
  const designItems = [
    { path: '/admin/forms', label: 'Form Builder', icon: FileText },
    { path: '/admin/design/vote-link', label: 'Vote Link', icon: Link2 },
    { path: '/admin/design/certificate', label: 'Certificate', icon: Award },
    { path: '/admin/design/flyer', label: 'Dynamic Flyer', icon: FileImage },
  ];

  const navItemsAfterDesign = [
    { path: '/admin/form-responses', label: 'Form Responses', icon: ClipboardList },
    { path: '/admin/votes', label: 'Votes', icon: Vote },
    { path: '/admin/media', label: 'Media', icon: ImageIcon },
    { path: '/admin/news', label: 'In News', icon: Newspaper },
    { path: '/admin/blogs', label: 'Education Blogs', icon: BookOpen },
    { path: '/admin/notifications', label: 'Notifications', icon: Bell },
  ];

  // Bank Details visible to Super Admin and Treasurer
  if (isSuperAdmin || isTreasurer) {
    navItemsAfterDesign.push({ path: '/admin/bank-details', label: 'Bank Details', icon: Building2 });
  }

  if (isSuperAdmin) {
    navItemsAfterDesign.push({ path: '/admin/settings', label: 'Settings', icon: Settings });
    navItemsAfterDesign.push({ path: '/admin/backup', label: 'Backup', icon: Database });
    navItemsAfterDesign.push({ path: '/admin/vote-recovery', label: 'Vote Recovery', icon: RefreshCw });
  }

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      toast.success('Logged out successfully');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      navigate('/', { replace: true });
    }
  };

  const isDesignActive = location.pathname.includes('/admin/design') || location.pathname === '/admin/forms';

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all hover:bg-white/10"
        style={isActive 
          ? { backgroundColor: '#D4AF37', color: '#0F172A', fontWeight: 500 } 
          : {}
        }
        data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="w-5 h-5" style={{ color: isActive ? '#0F172A' : '#D4AF37' }} />
        <span style={{ color: isActive ? '#0F172A' : '#FFFFFF' }}>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F1F5F9' }}>
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        style={{ backgroundColor: '#0F172A' }}
        data-testid="admin-sidebar"
      >
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-8 h-8" style={{ color: '#D4AF37' }} />
              <div>
                <h1 className="text-lg font-bold text-white">Admin Panel</h1>
                <p className="text-xs" style={{ color: '#D4AF37' }}>Jaipur Hawamahal</p>
              </div>
            </div>
          </div>
          
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {/* Main nav items before Design */}
            {navItems.map(renderNavItem)}
            
            {/* Design Section with Submenu */}
            <div className="space-y-1">
              <button
                onClick={() => setDesignMenuOpen(!designMenuOpen)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all hover:bg-white/10 ${isDesignActive ? 'bg-white/10' : ''}`}
                data-testid="admin-nav-design"
              >
                <div className="flex items-center gap-3">
                  <Palette className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <span style={{ color: '#FFFFFF' }}>Design</span>
                </div>
                {designMenuOpen ? (
                  <ChevronDown className="w-4 h-4" style={{ color: '#D4AF37' }} />
                ) : (
                  <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                )}
              </button>
              
              {designMenuOpen && (
                <div className="ml-4 space-y-1 border-l border-white/10 pl-4">
                  {designItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all hover:bg-white/10 text-sm"
                        style={isActive 
                          ? { backgroundColor: '#D4AF37', color: '#0F172A', fontWeight: 500 } 
                          : {}
                        }
                        data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className="w-4 h-4" style={{ color: isActive ? '#0F172A' : '#D4AF37' }} />
                        <span style={{ color: isActive ? '#0F172A' : '#FFFFFF' }}>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Nav items after Design */}
            {navItemsAfterDesign.map(renderNavItem)}
          </nav>
          
          <div className="p-4 border-t border-white/10 space-y-2">
            <a href={KANYA_VIVAH_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all" style={{ backgroundColor: '#8B1E2B', color: '#FFFFFF' }} data-testid="admin-kanya-vivah-link">
              <Heart className="w-5 h-5" fill="currentColor" style={{ color: '#D4AF37' }} /><span>101 Kanya Vivah</span>
            </a>
            <Link
              to="/app"
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 transition-all"
              data-testid="back-to-app-btn"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: '#D4AF37' }} />
              <span style={{ color: '#FFFFFF' }}>Back to App</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-500/20 transition-all w-full text-left"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" style={{ color: '#F87171' }} />
              <span style={{ color: '#F87171' }}>Logout</span>
            </button>
          </div>
        </div>
      </aside>
      
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-sidebar-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>
                {[...navItems, ...designItems, ...navItemsAfterDesign].find(item => item.path === location.pathname)?.label || 'Admin'}
              </h2>
            </div>
            <NotificationBell
              notifications={notifications}
              unreadCount={unreadCount}
              connected={connected}
              markAllRead={markAllRead}
              clearAll={clearAll}
            />
            {user && (
              <div className="text-right">
                <p className="text-sm font-medium" style={{ color: '#0F172A' }}>{user.name}</p>
                <p className="text-xs" style={{ color: '#D4AF37' }}>{user.role}</p>
              </div>
            )}
          </div>
        </header>
        
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
